/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { generateSimplestList, SimpleAttributeList, simpleLocalAttribute } from '@touchplan/common';
import { DefaultSplitDataRenderType, SplitData } from '@touchplan/common';
import { CustomFieldDropDownDefinition } from './CustomFieldDropDownDefinition';

export interface ICustomFieldDefinition {
	label: string;
	type: string;
	dataType?: string;
	unit?: string;
	listOfValuesId?: string; // from database, id points to a set of dropdown list items
	listOfValues?: CustomFieldDropDownDefinition; // joined property
	isEnabled?: boolean;
	ticketTypes?: string[];
	roles?: string[];
	order?: number;
	isRequired?: boolean;
}

export class CustomFieldDefinition extends SplitData<ICustomFieldDefinition, DefaultSplitDataRenderType> {
	protected useInPlace = true;
	protected attributeList: SimpleAttributeList = Object.assign(
		generateSimplestList([
			'label',
			'type',
			'dataType',
			'unit',
			'listOfValuesId',
			'isEnabled',
			'ticketTypes',
			'roles',
			'order',
			'isRequired',
		]),
		{
			listOfValues: simpleLocalAttribute,
		}
	);

	constructor(id: string) {
		super(id, DefaultSplitDataRenderType.database);
	}

	// called when we get data updates from firebase, i.e. 'child_added'
	public databaseUpdate(data: ICustomFieldDefinition): void {
		super.databaseUpdate(data);

		// convert ticketTypes and roles to array for UI
		this.database.ticketTypes = data.ticketTypes ? Object.keys(data.ticketTypes) : null;
		this.database.roles = data.roles ? Object.keys(data.roles) : null;
	}

	// called when we add item locally via addItem(), i.e. 'add_item'
	public localDatabaseUpdate(data: ICustomFieldDefinition): void {
		super.databaseUpdate(data);
	}

	public generateDatabaseOutput(renderType?: DefaultSplitDataRenderType): object {
		const view = super.generateDatabaseOutput(renderType);

		// convert ticketTypes and roles back to objects before persisting
		if (view.ticketTypes) {
			view.ticketTypes = view.ticketTypes.reduce((output: object, ticketType: string) => {
				output[ticketType] = true;
				return output;
			}, {});
		}

		if (view.roles) {
			view.roles = view.roles.reduce((output: object, role: string) => {
				output[role] = true;
				return output;
			}, {});
		}

		return view;
	}

	// this is used by a special case handling reorder existing custom field while its being edited
	// CustomFieldsApi.moveDefinition() has more info
	public getDatabaseFields(): ICustomFieldDefinition {
		return this.database;
	}

	// this is used for analytics to detect if isEnabled changed
	// note: in order for this check to work, persist use edit view and changeView after
	public didVisibilityChange(): boolean {
		return this.view.isEnabled !== this.database.isEnabled;
	}
}
